import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import { Badge } from "../components/Badge";
import { CheckCircle2, XCircle, RefreshCw, Search, RotateCw } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "pending", label: "Pending" },
  { k: "verified_pending", label: "Verified (Hybrid)" },
  { k: "approved", label: "Approved" },
  { k: "underpaid", label: "Underpaid" },
  { k: "failed", label: "Failed" },
  { k: "rejected", label: "Rejected" },
  { k: "", label: "All" },
];

const fmtBdt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function AgentUsdtDeposits() {
  const [tab, setTab] = useState("verified_pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // {type: 'approve'|'reject', dep}
  const paged = usePagedRows(rows, 12, [tab, q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab) params.set("status", tab);
      if (q.trim()) params.set("search", q.trim());
      const { data } = await api.get(`/agent-usdt-deposits?${params.toString()}`);
      setRows(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const counts = useMemo(() => ({ total: rows.length }), [rows]);

  return (
    <div data-testid="agent-usdt-deposits-page">
      <PageHeader
        title="Agent USDT Deposits"
        subtitle="Approve or reject USDT deposits submitted by agents from the agent panel. Approval credits the agent's BDT balance at the configured rate."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/agent-usdt-deposits/sync-pending");
                  toast.success(`Synced ${data.synced} pending deposits`);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Bulk sync failed");
                }
              }}
              className="h-9 px-3 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black rounded-sm text-[13px] font-semibold"
              data-testid="usdt-admin-bulk-sync"
              title="Poll NOWPayments for every pending deposit"
            >
              <RotateCw size={13} /> Sync All Pending
            </button>
            <button onClick={load} className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]" data-testid="usdt-admin-refresh">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        }
      />

      <div className="bg-white border border-gray-200 rounded-sm mb-4">
        <div className="flex items-center gap-1 border-b border-gray-100 px-2 py-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.k || "all"}
              onClick={() => setTab(t.k)}
              data-testid={`usdt-admin-tab-${t.k || "all"}`}
              className={`px-3 py-1.5 rounded-sm text-[13px] font-semibold ${
                tab === t.k ? "bg-emerald-500 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="flex-1" />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
            className="flex items-center gap-1 border border-gray-200 rounded-sm px-2 h-8"
          >
            <Search size={13} className="text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search agent / txid"
              className="text-[13px] w-56 outline-none bg-transparent"
              data-testid="usdt-admin-search"
            />
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left py-2.5 px-3 font-semibold">Date</th>
                <th className="text-left py-2.5 px-3 font-semibold">Agent</th>
                <th className="text-left py-2.5 px-3 font-semibold">Network</th>
                <th className="text-right py-2.5 px-3 font-semibold">USDT</th>
                <th className="text-right py-2.5 px-3 font-semibold">Rate</th>
                <th className="text-right py-2.5 px-3 font-semibold">BDT (expected/credited)</th>
                <th className="text-left py-2.5 px-3 font-semibold">Payment ID</th>
                <th className="text-left py-2.5 px-3 font-semibold">Status</th>
                <th className="text-right py-2.5 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-8 text-center text-gray-400">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-gray-400">No records</td></tr>
              ) : (
                paged.visible.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100" data-testid={`usdt-admin-row-${r.id}`}>
                    <td className="py-2 px-3 whitespace-nowrap text-gray-600">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">
                      <div className="font-mono text-emerald-700">{r.agent_username || "—"}</div>
                      <div className="text-[11px] text-gray-500">{r.agent_name}</div>
                    </td>
                    <td className="py-2 px-3 font-mono">{r.network}</td>
                    <td className="py-2 px-3 text-right font-mono">{Number(r.usd_amount).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono text-gray-500">{Number(r.rate_bdt_per_usd).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold text-emerald-700">
                      {fmtBdt(r.status === "approved" ? r.credited_bdt || r.expected_bdt : r.expected_bdt)}
                    </td>
                    <td className="py-2 px-3 font-mono text-gray-600 max-w-[200px] truncate" title={r.nowpayments_payment_id}>{r.nowpayments_payment_id || "—"}</td>
                    <td className="py-2 px-3">
                      <Badge
                        variant={
                          r.status === "approved"
                            ? "success"
                            : (r.status === "rejected" || r.status === "failed") && r.auto_rejected
                            ? "neutral"
                            : r.status === "rejected" || r.status === "failed"
                            ? "danger"
                            : r.status === "verified_pending"
                            ? "info"
                            : "warning"
                        }
                      >
                        {(r.status === "rejected" && r.auto_rejected) ? "auto-rejected" : r.status}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-right">
                      {(r.status === "pending" || r.status === "verified_pending") ? (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/agent-usdt-deposits/${r.id}/sync`);
                                toast.success(`Synced — status: ${data.status}`);
                                load();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Sync failed");
                              }
                            }}
                            className="h-8 px-2 inline-flex items-center gap-1 bg-amber-400/10 hover:bg-amber-400/20 text-amber-700 border border-amber-400/40 rounded-sm text-[12px]"
                            data-testid={`usdt-admin-sync-${r.id}`}
                            title="Poll NOWPayments now"
                          >
                            <RotateCw size={12} />
                          </button>
                          <button
                            onClick={() => setModal({ type: "approve", dep: r })}
                            className="h-8 px-2.5 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-[12px]"
                            data-testid={`usdt-admin-approve-${r.id}`}
                          >
                            <CheckCircle2 size={13} /> Approve
                          </button>
                          <button
                            onClick={() => setModal({ type: "reject", dep: r })}
                            className="h-8 px-2.5 inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-rose-300 hover:text-rose-600 text-gray-600 rounded-sm text-[12px]"
                            data-testid={`usdt-admin-reject-${r.id}`}
                          >
                            <XCircle size={13} /> Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-[11px]">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-3 py-2 border-t border-gray-100 text-[11px] text-gray-500">{counts.total} records</div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="agent-usdt-pg" />
      </div>

      {modal?.type === "approve" && (
        <ApproveModal dep={modal.dep} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
      {modal?.type === "reject" && (
        <RejectModal dep={modal.dep} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function ApproveModal({ dep, onClose, onDone }) {
  const [credited, setCredited] = useState(Number(dep.expected_bdt || 0));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!credited || credited <= 0) return toast.error("Credited BDT must be > 0");
    setBusy(true);
    try {
      await api.post(`/agent-usdt-deposits/${dep.id}/approve`, {
        credited_bdt: credited,
        admin_note: note || undefined,
      });
      toast.success(`Approved — ৳${credited.toFixed(2)} credited to ${dep.agent_username}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="usdt-approve-modal">
        <h3 className="text-lg font-bold mb-1">Approve USDT Deposit</h3>
        <p className="text-xs text-gray-500 mb-4">
          Agent <span className="font-mono">{dep.agent_username}</span> submitted{" "}
          <span className="font-mono font-semibold">{Number(dep.usd_amount).toFixed(2)} USDT</span> via{" "}
          <span className="font-mono">{dep.network}</span>.
        </p>

        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Credit BDT Amount</label>
        <input
          type="number"
          step="0.01"
          value={credited}
          onChange={(e) => setCredited(parseFloat(e.target.value) || 0)}
          className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
          data-testid="usdt-approve-amount"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Suggested: {Number(dep.usd_amount).toFixed(2)} USDT × rate {Number(dep.rate_bdt_per_usd).toFixed(2)} = ৳
          {Number(dep.expected_bdt).toFixed(2)}
        </p>

        <label className="block text-xs uppercase tracking-wider text-gray-500 mt-3 mb-1">Admin Note (optional)</label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
          data-testid="usdt-approve-note"
        />

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50"
            data-testid="usdt-approve-submit"
          >
            {busy ? "Approving..." : "Approve & Credit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ dep, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/agent-usdt-deposits/${dep.id}/reject`, { reason });
      toast.success("Rejected");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="usdt-reject-modal">
        <h3 className="text-lg font-bold mb-1">Reject USDT Deposit</h3>
        <p className="text-xs text-gray-500 mb-4">
          Reject <span className="font-mono font-semibold">{Number(dep.usd_amount).toFixed(2)} USDT</span> from{" "}
          <span className="font-mono">{dep.agent_username}</span>?
        </p>
        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Reason</label>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. TxID not found on-chain"
          className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
          data-testid="usdt-reject-reason"
        />
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="h-9 px-4 bg-rose-500 hover:bg-rose-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50"
            data-testid="usdt-reject-submit"
          >
            {busy ? "Rejecting..." : "Confirm Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}
